import { selectedFeatureAtom } from '@/atoms/selection.atom';
import { execute } from '@/graphql/execute';
import { updateFeatureMutation } from '@/queries/feature.query';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useRef } from 'react';
import { toast } from 'sonner';

export const useFeatureActions = () => {
  const queryClient = useQueryClient();
  const saveParamsRef = useRef<{ featureId: string; layerId: string } | null>(null);

  const saveFeature = useAtomCallback(
    useCallback(
      async (get, set) => {
        const params = saveParamsRef.current;
        if (!params) return;

        const feature = get(selectedFeatureAtom);
        if (!feature || feature.id !== params.featureId) return;

        const t = toast.loading('Saving feature...');
        try {
          await execute(updateFeatureMutation, {
            input: {
              id: feature.id,
              geometry: feature.geometry,
              properties: feature.properties,
            },
          });

          toast.success('Feature saved', { id: t });
          set(selectedFeatureAtom, (prev) => (prev ? { ...prev, isMoving: false, isEditing: false } : null));
          queryClient.invalidateQueries({ queryKey: ['features', params.layerId] });
        } catch (error) {
          console.error(error);
          toast.error('Failed to save feature', {
            id: t,
            description: 'Please try again. If the problem persists, please contact support.',
          });
        }
      },
      [queryClient],
    ),
  );

  const createUpdateHandler = useCallback(
    (featureId: string, layerId: string) => {
      saveParamsRef.current = { featureId, layerId };
      return saveFeature;
    },
    [saveFeature],
  );

  const selectFeatureForEditing = useAtomCallback(
    useCallback(
      (_get, set, feature: any) => {
        set(selectedFeatureAtom, {
          ...feature,
          isEditing: true,
          onSave: createUpdateHandler(feature.id, feature.layerId),
        });
      },
      [createUpdateHandler],
    ),
  );

  return {
    createUpdateHandler,
    selectFeatureForEditing,
  };
};
